import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AutoComplete, Button, Card, Col, DatePicker, Form, Input, InputNumber, Modal,
  Popconfirm, Row, Select, Space, Statistic, Table, Tag, Tooltip, Typography, Upload, message,
} from 'antd';
import {
  CheckCircleOutlined, DeleteOutlined, EditOutlined, PaperClipOutlined, PlusOutlined,
  ReloadOutlined, RollbackOutlined, SafetyCertificateOutlined, UploadOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../utils/config';
import { authHeaders } from '../../utils/auth';
import { formatMoney, inputNumberMoneyProps } from '../../utils/currency';
import { useTheme } from '../../context/ThemeContext';

const { Title, Text } = Typography;
const { TextArea } = Input;

const TUR_ETIKET = {
  ruhsat: { label: 'Ruhsat', color: 'purple' },
  ihale: { label: 'İhale', color: 'geekblue' },
  kira: { label: 'Kira', color: 'cyan' },
  diger: { label: 'Diğer', color: 'default' },
};

const DURUM_SECENEKLERI = [
  { value: 'all', label: 'Tümü' },
  { value: 'bekleyen', label: 'İade Bekleyen' },
  { value: 'gecikmis', label: 'Gecikmiş' },
  { value: 'iade_alindi', label: 'İade Alınan' },
];

const tarihMetni = (d) => (d ? dayjs(d).format('DD.MM.YYYY') : '-');
const hataMetni = (e, varsayilan) => e?.response?.data?.detail || varsayilan;

/** Form → API gövdesi (DatePicker dayjs → 'YYYY-MM-DD'). */
const formdanGovde = (v) => ({
  lokasyon: v.lokasyon ? Number(v.lokasyon) : null,
  tur: v.tur || 'ruhsat',
  kurum: v.kurum || null,
  banka: v.banka || null,
  mektup_no: v.mektup_no || null,
  tutar: v.tutar === undefined || v.tutar === null || v.tutar === '' ? null : Number(v.tutar),
  verilis_tarihi: v.verilis_tarihi ? v.verilis_tarihi.format('YYYY-MM-DD') : null,
  iade_tarihi: v.iade_tarihi ? v.iade_tarihi.format('YYYY-MM-DD') : null,
  aciklama: v.aciklama || null,
});

/**
 * Teminat mektubu takibi (20.08 madde 5).
 *
 * Ruhsat bedeli formundan otomatik açılan 'ruhsat' kayıtları ile elle girilen
 * ihale/kira/diğer teminatları tek listede gösterir; iade tarihi yaklaşan ve
 * geciken mektuplar üstte ve renkli etiketle görünür.
 *
 * @param {boolean} gomulu - Sekme içinde gösteriliyorsa dış dolgu/başlık kırpılır
 */
export default function TeminatMektubuTab({ gomulu = false }) {
  const { darkMode } = useTheme();
  const [satirlar, setSatirlar] = useState([]);
  const [ozet, setOzet] = useState(null);
  const [uyari, setUyari] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [durum, setDurum] = useState('all');
  const [arama, setArama] = useState('');
  const [aramaMetni, setAramaMetni] = useState('');

  // Kayıt formu (yeni / düzenle)
  const [formAcik, setFormAcik] = useState(false);
  const [duzenlenen, setDuzenlenen] = useState(null);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [form] = Form.useForm();
  const [lokasyonSecenekleri, setLokasyonSecenekleri] = useState([]);
  const [lokasyonMetni, setLokasyonMetni] = useState('');

  // İade modalı
  const [iadeKaydi, setIadeKaydi] = useState(null);
  const [iadeForm] = Form.useForm();
  const [iadeGonderiliyor, setIadeGonderiliyor] = useState(false);

  const [islemId, setIslemId] = useState(null);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/teminat/liste`, {
        ...authHeaders(),
        params: { durum, q: arama || undefined },
      });
      setSatirlar(data?.satirlar || []);
      setOzet(data?.ozet || null);
      setUyari(data?.uyari || null);
    } catch (e) {
      message.error(hataMetni(e, 'Teminat listesi yüklenemedi'));
    } finally {
      setYukleniyor(false);
    }
  }, [durum, arama]);

  useEffect(() => { yukle(); }, [yukle]);

  // ---- lokasyon arama (form) ----
  const lokasyonAra = useCallback(async (q) => {
    setLokasyonMetni(q);
    if (!q || q.trim().length < 2) { setLokasyonSecenekleri([]); return; }
    try {
      const { data } = await axios.get(`${API_BASE_URL}/teminat/search-lokasyon`, {
        ...authHeaders(), params: { q: q.trim() },
      });
      setLokasyonSecenekleri((data?.data || []).map((l) => ({
        value: String(l.MaptriksID),
        label: `${l.lokasyon_ad || ''} (${l.MaptriksID})${l.il_ad ? ` — ${l.il_ad}` : ''}`,
        ad: l.lokasyon_ad,
      })));
    } catch (e) {
      // sessiz: arama yardımcı özelliktir
    }
  }, []);

  const lokasyonSec = (value, option) => {
    form.setFieldValue('lokasyon', value);
    setLokasyonMetni(`${option?.ad || ''} (${value})`);
  };

  // ---- yeni / düzenle ----
  const yeniAc = () => {
    setDuzenlenen(null);
    form.resetFields();
    form.setFieldsValue({ tur: 'ruhsat', verilis_tarihi: dayjs() });
    setLokasyonMetni('');
    setLokasyonSecenekleri([]);
    setFormAcik(true);
  };

  const duzenleAc = (kayit) => {
    setDuzenlenen(kayit);
    form.setFieldsValue({
      lokasyon: kayit.lokasyon ?? undefined,
      tur: kayit.tur || 'ruhsat',
      kurum: kayit.kurum || '',
      banka: kayit.banka || '',
      mektup_no: kayit.mektup_no || '',
      tutar: kayit.tutar ?? null,
      verilis_tarihi: kayit.verilis_tarihi ? dayjs(kayit.verilis_tarihi) : null,
      iade_tarihi: kayit.iade_tarihi ? dayjs(kayit.iade_tarihi) : null,
      aciklama: kayit.aciklama || '',
    });
    setLokasyonMetni(kayit.lokasyon ? `${kayit.lokasyon_adi || ''} (${kayit.lokasyon})` : '');
    setLokasyonSecenekleri([]);
    setFormAcik(true);
  };

  const kaydet = async () => {
    let degerler;
    try { degerler = await form.validateFields(); } catch (_) { return; }
    const govde = formdanGovde(degerler);
    setKaydediliyor(true);
    try {
      if (duzenlenen) {
        await axios.put(`${API_BASE_URL}/teminat/${duzenlenen.id}`, govde, authHeaders());
        message.success('Teminat güncellendi');
      } else {
        await axios.post(`${API_BASE_URL}/teminat`, govde, authHeaders());
        message.success('Teminat eklendi');
      }
      setFormAcik(false);
      yukle();
    } catch (e) {
      message.error(hataMetni(e, 'Kayıt başarısız'));
    } finally {
      setKaydediliyor(false);
    }
  };

  // ---- iade ----
  const iadeAc = (kayit) => {
    setIadeKaydi(kayit);
    iadeForm.setFieldsValue({ iade_alinma_tarihi: dayjs(), aciklama: '' });
  };

  const iadeGonder = async () => {
    let v;
    try { v = await iadeForm.validateFields(); } catch (_) { return; }
    setIadeGonderiliyor(true);
    try {
      await axios.post(`${API_BASE_URL}/teminat/${iadeKaydi.id}/iade`, {
        iade_alinma_tarihi: v.iade_alinma_tarihi ? v.iade_alinma_tarihi.format('YYYY-MM-DD') : null,
        aciklama: v.aciklama || null,
      }, authHeaders());
      message.success('İade alındı olarak işaretlendi');
      setIadeKaydi(null);
      yukle();
    } catch (e) {
      message.error(hataMetni(e, 'İade işaretlenemedi'));
    } finally {
      setIadeGonderiliyor(false);
    }
  };

  const iadeGeriAl = async (kayit) => {
    setIslemId(kayit.id);
    try {
      await axios.post(`${API_BASE_URL}/teminat/${kayit.id}/iade-geri-al`, {}, authHeaders());
      message.success('İade işareti kaldırıldı');
      yukle();
    } catch (e) {
      message.error(hataMetni(e, 'İşlem başarısız'));
    } finally {
      setIslemId(null);
    }
  };

  const sil = async (kayit) => {
    setIslemId(kayit.id);
    try {
      await axios.delete(`${API_BASE_URL}/teminat/${kayit.id}`, authHeaders());
      message.success('Teminat silindi');
      yukle();
    } catch (e) {
      message.error(hataMetni(e, 'Silinemedi'));
    } finally {
      setIslemId(null);
    }
  };

  // ---- dosya ----
  const dosyaAc = async (kayit) => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/teminat/${kayit.id}/dosya`, authHeaders());
      if (data?.url) window.open(data.url, '_blank', 'noopener');
      else message.warning('Dosya bağlantısı alınamadı');
    } catch (e) {
      message.error(hataMetni(e, 'Dosya açılamadı'));
    }
  };

  const dosyaYukle = async (kayit, file) => {
    const izinli = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!izinli.includes(file.type)) {
      message.error('Yalnızca PDF, JPG, PNG veya WebP yüklenebilir');
      return false;
    }
    if (file.size > 10 * 1024 * 1024) {
      message.error('Dosya 10 MB sınırını aşıyor');
      return false;
    }
    const fd = new FormData();
    fd.append('dosya', file);
    setIslemId(kayit.id);
    try {
      await axios.post(`${API_BASE_URL}/teminat/${kayit.id}/dosya`, fd, {
        headers: { ...(authHeaders().headers || {}), 'Content-Type': 'multipart/form-data' },
      });
      message.success('Dosya yüklendi');
      yukle();
    } catch (e) {
      message.error(hataMetni(e, 'Dosya yüklenemedi'));
    } finally {
      setIslemId(null);
    }
    return false; // antd Upload kendi isteğini atmasın
  };

  // ---- kolonlar ----
  const kolonlar = useMemo(() => [
    {
      title: 'Lokasyon',
      dataIndex: 'lokasyon_adi',
      key: 'lokasyon',
      render: (_, r) => (r.lokasyon ? (
        <div>
          <div>{r.lokasyon_adi || <Text type="secondary">(ad yok)</Text>}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>{r.lokasyon}</Text>
        </div>
      ) : <Text type="secondary">-</Text>),
    },
    {
      title: 'Tür',
      dataIndex: 'tur',
      key: 'tur',
      width: 90,
      render: (t) => {
        const m = TUR_ETIKET[t] || { label: t || '-', color: 'default' };
        return <Tag color={m.color}>{m.label}</Tag>;
      },
    },
    { title: 'Kurum', dataIndex: 'kurum', key: 'kurum', render: (v) => v || '-' },
    { title: 'Banka', dataIndex: 'banka', key: 'banka', render: (v) => v || '-' },
    { title: 'Mektup No', dataIndex: 'mektup_no', key: 'mektup_no', render: (v) => v || '-' },
    {
      title: 'Tutar',
      dataIndex: 'tutar',
      key: 'tutar',
      align: 'right',
      render: (v) => formatMoney(v),
      sorter: (a, b) => (a.tutar || 0) - (b.tutar || 0),
    },
    { title: 'Veriliş', dataIndex: 'verilis_tarihi', key: 'verilis', width: 100, render: tarihMetni },
    {
      title: 'İade Tarihi',
      dataIndex: 'iade_tarihi',
      key: 'iade_tarihi',
      render: (v, r) => {
        if (!v) return '-';
        let etiket = null;
        if (!r.iade_alindi && r.kalan_gun !== null && r.kalan_gun !== undefined) {
          if (r.gecikmis || r.kalan_gun < 0) {
            etiket = <Tag color="red">{Math.abs(r.kalan_gun)} gün gecikti</Tag>;
          } else if (r.kalan_gun <= 15) {
            etiket = <Tag color="gold">{r.kalan_gun === 0 ? 'Bugün' : `${r.kalan_gun} gün kaldı`}</Tag>;
          } else {
            etiket = <Tag color="green">{r.kalan_gun} gün kaldı</Tag>;
          }
        }
        return <Space size={4}>{tarihMetni(v)}{etiket}</Space>;
      },
    },
    {
      title: 'Durum',
      dataIndex: 'iade_alindi',
      key: 'durum',
      width: 120,
      render: (v, r) => (v ? (
        <Tooltip title={r.iade_alinma_tarihi ? `İade: ${tarihMetni(r.iade_alinma_tarihi)}` : ''}>
          <Tag color="green" icon={<CheckCircleOutlined />}>İade Alındı</Tag>
        </Tooltip>
      ) : <Tag color="blue">Bekliyor</Tag>),
    },
    {
      title: 'Dosya',
      dataIndex: 'dosya',
      key: 'dosya',
      width: 70,
      align: 'center',
      render: (v, r) => (v ? (
        <Tooltip title={r.dosya_adi || 'Dosyayı aç'}>
          <Button type="link" size="small" icon={<PaperClipOutlined />} onClick={() => dosyaAc(r)} />
        </Tooltip>
      ) : <Text type="secondary">-</Text>),
    },
    {
      title: 'Ekleyen',
      dataIndex: 'ekleyen_adi',
      key: 'ekleyen',
      render: (v, r) => (
        <div>
          <div>{v || (r.enerji_takip_id ? 'Enerji süreci' : '-')}</div>
          {r.enerji_takip_id ? <Text type="secondary" style={{ fontSize: 11 }}>otomatik (ruhsat)</Text> : null}
        </div>
      ),
    },
    {
      title: 'İşlem',
      key: 'islem',
      fixed: 'right',
      render: (_, r) => (
        <Space size={2} wrap>
          <Tooltip title="Düzenle">
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => duzenleAc(r)} />
          </Tooltip>
          {!r.iade_alindi ? (
            <Tooltip title="İade alındı">
              <Button size="small" type="text" icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                onClick={() => iadeAc(r)} />
            </Tooltip>
          ) : (
            <Popconfirm title="İade işareti kaldırılsın mı?" okText="Evet" cancelText="Vazgeç"
              onConfirm={() => iadeGeriAl(r)}>
              <Tooltip title="İadeyi geri al">
                <Button size="small" type="text" icon={<RollbackOutlined />} loading={islemId === r.id} />
              </Tooltip>
            </Popconfirm>
          )}
          <Upload accept=".pdf,.jpg,.jpeg,.png,.webp" showUploadList={false}
            beforeUpload={(file) => dosyaYukle(r, file)}>
            <Tooltip title={r.dosya ? 'Dosyayı değiştir' : 'Dosya yükle'}>
              <Button size="small" type="text" icon={<UploadOutlined />} loading={islemId === r.id} />
            </Tooltip>
          </Upload>
          <Popconfirm title="Teminat kaydı silinsin mi?" okText="Sil" okButtonProps={{ danger: true }}
            cancelText="Vazgeç" onConfirm={() => sil(r)}>
            <Tooltip title="Sil">
              <Button size="small" type="text" danger icon={<DeleteOutlined />} loading={islemId === r.id} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [islemId, yukle]);

  const satirSinifi = (r) => (r.gecikmis ? 'teminat-gecikmis' : '');

  return (
    <div style={{ padding: gomulu ? 0 : 16 }}>
      <style>{`.teminat-gecikmis td { background: rgba(255, 77, 79, 0.08) !important; }`}</style>
      <Card
        className={darkMode ? 'dark-card' : ''}
        bordered={!gomulu}
        title={
          <Space wrap>
            <SafetyCertificateOutlined />
            {!gomulu && <Title level={4} style={{ margin: 0 }}>Teminat Mektupları</Title>}
            {gomulu && <span>Teminat Mektupları</span>}
            {ozet && <Tag>Toplam: {ozet.toplam_adet}</Tag>}
          </Space>
        }
        extra={
          <Space wrap>
            <Select
              style={{ width: 170 }}
              value={durum}
              options={DURUM_SECENEKLERI}
              onChange={setDurum}
            />
            <Input.Search
              allowClear
              placeholder="Lokasyon, kurum, banka, mektup no"
              style={{ width: 260 }}
              value={aramaMetni}
              onChange={(e) => { setAramaMetni(e.target.value); if (!e.target.value) setArama(''); }}
              onSearch={(v) => setArama((v || '').trim())}
            />
            <Button icon={<ReloadOutlined />} onClick={yukle} loading={yukleniyor}>Yenile</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={yeniAc}>Yeni Teminat</Button>
          </Space>
        }
      >
        {uyari && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={uyari} />
        )}

        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col xs={12} md={6}>
            <Card size="small" className={darkMode ? 'dark-card' : ''}>
              <Statistic title="İade Bekleyen (adet)" value={ozet?.bekleyen_adet ?? 0}
                valueStyle={{ color: '#1677ff' }} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small" className={darkMode ? 'dark-card' : ''}>
              <Statistic title="İade Bekleyen (tutar)" value={formatMoney(ozet?.bekleyen_tutar ?? 0)}
                valueStyle={{ color: '#1677ff', fontSize: 20 }} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small" className={darkMode ? 'dark-card' : ''}>
              <Statistic title="Gecikmiş" value={ozet?.gecikmis_adet ?? 0}
                valueStyle={{ color: (ozet?.gecikmis_adet || 0) > 0 ? '#ff4d4f' : undefined }} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small" className={darkMode ? 'dark-card' : ''}>
              <Statistic title="İade Alınan" value={ozet?.iade_alinan_adet ?? 0}
                valueStyle={{ color: '#52c41a' }} />
            </Card>
          </Col>
        </Row>

        <Table
          rowKey="id"
          size="small"
          loading={yukleniyor}
          columns={kolonlar}
          dataSource={satirlar}
          rowClassName={satirSinifi}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 25, showSizeChanger: false, showTotal: (t) => `${t} kayıt` }}
          locale={{ emptyText: 'Kayıt yok' }}
        />
      </Card>

      {/* Yeni / düzenle */}
      <Modal
        open={formAcik}
        title={duzenlenen ? `Teminat düzenle (#${duzenlenen.id})` : 'Yeni teminat mektubu'}
        onCancel={() => setFormAcik(false)}
        onOk={kaydet}
        okText={duzenlenen ? 'Güncelle' : 'Kaydet'}
        cancelText="Vazgeç"
        confirmLoading={kaydediliyor}
        destroyOnClose
        width={640}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="lokasyon" hidden><Input /></Form.Item>
          <Form.Item label="Lokasyon" extra="Ad ya da MaptriksID ile arayın; boş bırakılabilir.">
            <AutoComplete
              value={lokasyonMetni}
              options={lokasyonSecenekleri}
              onSearch={lokasyonAra}
              onSelect={lokasyonSec}
              onChange={(v) => {
                setLokasyonMetni(v);
                if (!v) form.setFieldValue('lokasyon', undefined);
              }}
              placeholder="Lokasyon ara"
              allowClear
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="tur" label="Tür" rules={[{ required: true, message: 'Tür seçin' }]}>
                <Select options={Object.entries(TUR_ETIKET).map(([value, m]) => ({ value, label: m.label }))} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="kurum" label="Kurum" rules={[{ required: true, message: 'Kurum girin' }]}>
                <Input maxLength={150} placeholder="Mektubun verildiği kurum" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="banka" label="Banka">
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="mektup_no" label="Mektup No">
                <Input maxLength={60} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="tutar" label="Tutar (₺)"
                rules={[{ type: 'number', min: 0, message: 'Tutar negatif olamaz' }]}>
                <InputNumber {...inputNumberMoneyProps} min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="verilis_tarihi" label="Veriliş Tarihi">
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="iade_tarihi" label="İade (Vade) Tarihi">
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="aciklama" label="Açıklama">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* İade alındı */}
      <Modal
        open={!!iadeKaydi}
        title={iadeKaydi ? `İade alındı — ${iadeKaydi.kurum || ''} ${iadeKaydi.mektup_no ? `(${iadeKaydi.mektup_no})` : ''}` : ''}
        onCancel={() => setIadeKaydi(null)}
        onOk={iadeGonder}
        okText="İade Alındı"
        cancelText="Vazgeç"
        confirmLoading={iadeGonderiliyor}
        destroyOnClose
      >
        <Form form={iadeForm} layout="vertical" preserve={false}>
          <Form.Item name="iade_alinma_tarihi" label="İade alınma tarihi"
            rules={[{ required: true, message: 'Tarih seçin' }]}>
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="aciklama" label="Not (isteğe bağlı)">
            <TextArea rows={2} placeholder="Örn. aslı kuruma teslim edildi" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
